import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO, canonicalUrl, clampDescription, shareImage } from '@/config/seo';

/**
 * Jedina tačka za <head> po stranici: title, opis, kanonska URL, Open Graph,
 * Twitter i JSON-LD. Stranice zadaju samo ono što im je specifično —
 * sve ostalo dolazi iz `SEO` podrazumevanih vrednosti.
 *
 * @param {string}  title        bez imena firme; dodaje se automatski
 * @param {string}  description
 * @param {string}  path         putanja na sajtu (`/o-nama`) → canonical + og:url
 * @param {string}  [image]      public putanja ili apsolutan URL; default OG slika
 * @param {string}  [imageAlt]
 * @param {string}  [type]       og:type — `website`, `article`, `product`
 * @param {boolean} [noindex]    strane koje ne smeju u pretragu (korpa, checkout, admin)
 * @param {boolean} [nofollow]
 * @param {object|object[]} [jsonLd] structured data za ovu stranicu
 */
const Seo = ({
  title,
  description,
  path,
  image,
  imageAlt,
  type = 'website',
  noindex = false,
  nofollow = false,
  jsonLd,
  children,
}) => {
  const fullTitle = title
    ? title.includes(SEO.titleSuffix)
      ? title
      : `${title} | ${SEO.titleSuffix}`
    : SEO.titleSuffix;

  const desc = clampDescription(description);
  const url = path === undefined ? undefined : canonicalUrl(path);
  const img = shareImage(image);
  const isDefaultImage = !image;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  const robots = noindex
    ? `noindex,${nofollow ? 'nofollow' : 'follow'}`
    : `index,${nofollow ? 'nofollow' : 'follow'},max-image-preview:large,max-snippet:-1`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="robots" content={robots} />
      {desc && <meta name="description" content={desc} />}
      {url && <link rel="canonical" href={url} />}

      <meta property="og:site_name" content={SEO.siteName} />
      <meta property="og:locale" content={SEO.locale} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {desc && <meta property="og:description" content={desc} />}
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={img} />
      <meta property="og:image:secure_url" content={img} />
      <meta property="og:image:alt" content={imageAlt || SEO.defaultImageAlt} />
      {/* Dimenzije važe samo za našu OG sliku; slike proizvoda su raznih formata. */}
      {isDefaultImage && <meta property="og:image:type" content="image/png" />}
      {isDefaultImage && <meta property="og:image:width" content="1200" />}
      {isDefaultImage && <meta property="og:image:height" content="630" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {desc && <meta name="twitter:description" content={desc} />}
      <meta name="twitter:image" content={img} />
      <meta name="twitter:image:alt" content={imageAlt || SEO.defaultImageAlt} />

      {blocks.map((block, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <script key={index} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default Seo;
